import { Crepe } from '@milkdown/crepe';
import { defaultValueCtx, editorViewCtx, remarkPluginsCtx, schemaCtx } from '@milkdown/kit/core';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import React from 'react';
import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/frame.css';
import './MarkdownEditor.css';
import { CardToolbar } from './CardToolbar';
import { groupedCardsPlugin } from './plugins/groupedCards';
import { singleCardPlugin } from './plugins/singleCard';
import './plugins/groupedCards/groupedCards.css';
import '../../../styles/cards.css';

interface MarkdownEditorProps {
  value: string;
  onChange?: (markdown: string) => void;
  readOnly?: boolean;
  showCardToolbar?: boolean;
}

const CrepeEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  showCardToolbar = false,
}) => {
  const onChangeRef = React.useRef(onChange);

  // Keep the onChange ref up to date without causing re-renders
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEditor(
    (root) => {
      const crepe = new Crepe({
        root,
        defaultValue: value,
        features: {
          [Crepe.Feature.CodeMirror]: true,
          [Crepe.Feature.ListItem]: true,
          [Crepe.Feature.BlockEdit]: true,
          [Crepe.Feature.Cursor]: true,
          [Crepe.Feature.LinkTooltip]: true,
          [Crepe.Feature.ImageBlock]: true,
          [Crepe.Feature.Table]: true,
          [Crepe.Feature.Toolbar]: !readOnly,
        },
      });

      // IMPORTANT: Load plugins BEFORE creating the editor
      console.log('[MarkdownEditor] registering grouped-cards plugins', groupedCardsPlugin.length);
      groupedCardsPlugin.forEach((plugin, idx) => {
        try {
          console.log('[MarkdownEditor] use grouped-cards plugin', idx, plugin);
          crepe.editor.use(plugin as unknown as Parameters<typeof crepe.editor.use>[0]);
        } catch (err) {
          console.error('[MarkdownEditor] failed to register grouped-cards plugin', idx, err);
        }
      });

      console.log('[MarkdownEditor] registering single-card plugins', singleCardPlugin.length);
      singleCardPlugin.forEach((plugin, idx) => {
        try {
          console.log('[MarkdownEditor] use single-card plugin', idx, plugin);
          crepe.editor.use(plugin as unknown as Parameters<typeof crepe.editor.use>[0]);
        } catch (err) {
          console.error('[MarkdownEditor] failed to register single-card plugin', idx, err);
        }
      });

      // Register the listener BEFORE creating the editor
      console.log('[MarkdownEditor] registering markdownUpdated listener');
      crepe.on((listener) => {
        console.log('[MarkdownEditor] listener callback invoked');
        listener.markdownUpdated((ctx, markdown) => {
          console.log('[MarkdownEditor] markdownUpdated fired', {
            markdownLength: markdown.length,
            markdownStart: markdown.substring(0, 50),
            hasCallback: !!onChangeRef.current,
            ctx: ctx,
          });
          onChangeRef.current?.(markdown);
        });
        console.log('[MarkdownEditor] markdownUpdated listener registered');
      });

      // Create the editor AFTER plugins and listeners are registered
      crepe.editor
        .create()
        .then(() => {
          console.log('[MarkdownEditor] editor created successfully');
          crepe.editor.action((ctx) => {
            try {
              console.log(
                '[MarkdownEditor] remark plugins count',
                ctx.get(remarkPluginsCtx).length
              );
              console.log('[MarkdownEditor] schema nodes', Object.keys(ctx.get(schemaCtx).nodes));
              console.log('[MarkdownEditor] default value', ctx.get(defaultValueCtx));
              console.log('[MarkdownEditor] editor view ready', !!ctx.get(editorViewCtx));
            } catch (err) {
              console.error('[MarkdownEditor] debug action failed', err);
            }
          });
        })
        .catch((err) => console.error('[MarkdownEditor] create failed', err));

      if (readOnly) {
        crepe.setReadonly(true);
      }

      return crepe;
    },
    [readOnly]
  );

  return (
    <div className="milkdown-editor-wrapper">
      {!readOnly && showCardToolbar && <CardToolbar />}
      <Milkdown />
    </div>
  );
};

export const MarkdownEditor: React.FC<MarkdownEditorProps> = (props) => {
  return (
    <MilkdownProvider>
      <CrepeEditor {...props} />
    </MilkdownProvider>
  );
};
